import React from 'preact/compat';
import { Button, ModalWrapper } from 'list/DesignSystem';
import { Sheets, ModalBody, ModalHeader, ModalFooter } from '@ingka/modal';
import { h } from 'preact';
import { useState } from 'preact/hooks';
import { FavouritesPayload, publish } from 'helpers/pubsub';
import { createList } from 'adapters/list';
import { List } from 'adapters/list/graphqlTypes';
import documentAddPath from '@ingka/ssr-icon/paths/document-add';
import Lists from 'apps/AddToListModal/components/Lists';
import CreateList from 'apps/AddToListModal/components/CreateList';
import translate from 'list/utils/translate';
import { track } from 'helpers/optimizely';
import { ADD_ITEMS_ABORTED } from 'list/pubsub';
import { addToList } from 'apps/AddToListModal/addToList';

type View = 'lists' | 'createList';
type Props = {
    destroy: () => void;
    lists: List[];
    payload: FavouritesPayload;
};

export const AddToListModal = ({ destroy, lists, payload }: Props) => {
    const [selectedView, setSelectedView] = useState<View>('lists');
    const [listName, setListName] = useState('');
    const [error, setError] = useState('');
    const [shouldValidate, setShouldValidate] = useState(false);
    let modalTitle;

    const onAddToNewList = async () => {
        setShouldValidate(true);
        // Frontend validation
        if (lists.find(list => list.name === listName)) {
            setError(translate('NAME_CONFLICT'));
            return;
        }
        if (lists.length === 100) {
            setError(translate('MAX_CREATE_LIMIT'));
            return;
        }
        // Backend validation in case a new list was created in another tab
        createList(listName)
            .then(async response => {
                track('favourites_lists_created');
                const { name, listId } = response.data.list;
                await addToList(payload, listId, name);
                destroy();
            })
            .catch(e => {
                if (e.message.includes('NAME_CONFLICT')) {
                    setError(translate('NAME_CONFLICT'));
                } else if (e.message.includes('MAX_CREATE_LIMIT')) {
                    setError(translate('MAX_CREATE_LIMIT'));
                } else {
                    setError(translate('GENERIC_ERROR'));
                }
            });
    };

    const onSetListName = (listName: string) => {
        setError('');
        setShouldValidate(false);
        setListName(listName);
    };
    if (lists.length <= 1 && !payload.alwaysShowModal) {
        return null;
    }

    if (selectedView === 'lists') {
        if ('items' in payload) {
            modalTitle = translate('Save items in list');
        } else {
            modalTitle = translate('Save item in list', {
                name: payload.productName,
            });
        }
    } else {
        modalTitle = translate('Create list modal title');
    }

    const handleCloseBtn = () => {
        publish(ADD_ITEMS_ABORTED, payload);
        destroy();
    };
    return (
        <ModalWrapper visible={true} handleCloseBtn={handleCloseBtn}>
            <Sheets
                header={
                    <ModalHeader
                        title={modalTitle}
                        backBtnClick={
                            selectedView === 'lists'
                                ? null
                                : () => {
                                      if (selectedView === 'createList') {
                                          setSelectedView('lists');
                                      }
                                  }
                        }
                    />
                }
                footer={
                    <ModalFooter>
                        {selectedView === 'lists' && (
                            <Button
                                key="create-list"
                                type="primary"
                                htmlType="submit"
                                text={translate('Create list')}
                                disabled={false}
                                onClick={() => setSelectedView('createList')}
                                loading={false}
                                ssrIcon={documentAddPath}
                            />
                        )}
                        {selectedView === 'createList' && (
                            <Button
                                key="create-list"
                                type="primary"
                                htmlType="submit"
                                text={translate('Create list confirmation button')}
                                disabled={listName === ''}
                                onClick={onAddToNewList}
                                loading={false}
                            />
                        )}
                    </ModalFooter>
                }
            >
                <ModalBody>
                    {selectedView === 'lists' && (
                        <Lists
                            addToList={async (payload: FavouritesPayload, listId?: string, name?: string) => {
                                await addToList(payload, listId, name);
                                destroy();
                            }}
                            lists={lists}
                            payload={payload}
                        />
                    )}
                    {selectedView === 'createList' && (
                        <CreateList
                            error={error}
                            listName={listName}
                            shouldValidate={shouldValidate}
                            setListName={onSetListName}
                        />
                    )}
                </ModalBody>
            </Sheets>
        </ModalWrapper>
    );
};
export default AddToListModal;
